import * as THREE from "three";
import { Cell } from "@highmarch/domain";
import { GroundMesh } from "../groundMesh";

/** The critters' tuning (critter.ts, critters.ts, critterHub.ts): gaits, postures, timings, sizes and small maths. */

export type CritterKind = "rabbit" | "bird";

export const RABBIT_COATS = [0x9c7a55, 0xb8b0a4, 0xf0e6d6, 0x6e5238];
/** Sparrow and thrush browns. The primary-coloured birds read as rubber ducks from the play camera (terrain F9). */
export const BIRD_COATS = [0x7a5a3e, 0x8b6a48, 0x5e4632, 0x9a7a58];

/**
 * How each kind gets about, in world units and seconds. A rabbit covers a cell in five or six short bounds with a
 * breath between them, not one long glide; fleeing, the bounds lengthen and the pauses go. A bird hops two-footed
 * over short distances and flutters across longer ones.
 */
export const GAIT = {
  rabbit: { hop: 0.2, time: 0.3, height: 0.06, rest: [0.04, 0.22], fleeHop: 0.42, fleeTime: 0.26, fleeHeight: 0.1 },
  bird: { hop: 0.07, time: 0.17, height: 0.03, rest: [0.05, 0.16], fleeHop: 0, fleeTime: 0, fleeHeight: 0 },
} as const;
/** A bird flutters rather than hops to a spot further than this, in a low arc of `FlutterHeight` over `FlutterTime`. */
export const BIRD_FLUTTER_OVER = 0.45;
export const FLUTTER_HEIGHT = 0.25;
export const FLUTTER_TIME = 0.7;
/** How far inside the destination a critter counts as arrived, world units. */
export const ARRIVED = 0.03;
/** Idle turns: how fast the yaw and the posture blend to where they are going (per second, exponential). */
export const TURN_RATE = 14;
export const POSE_RATE = 9;
/** Seconds each idle activity lasts, [min, max]. */
export const ACT_TIME: Record<IdleAct, [number, number]> = { nibble: [1.6, 3.6], look: [1, 2.2], still: [0.6, 1.6] };
/** Chance that an idle critter moves on when an activity ends (otherwise it starts another). */
export const MOVE_ON = 0.45;
/** Rabbit postures: sitting up to look round (negative pitch rears back on the hind feet), and the nibbling nod. */
export const SIT_UP = -0.38;
export const NIBBLE_HEAD = 0.6;
export const NIBBLE_CHEW = 0.08;
export const LOOK_SWEEP = 0.55;
/** Ears: upright rest, laid back in the air, flat when fleeing, and a flick now and then. */
export const EAR_UP = -0.15;
export const EAR_BACK = -0.6;
export const EAR_FLAT = -0.95;
export const EAR_FLICK = 0.45;
export const EAR_FLICK_EVERY: [number, number] = [0.8, 3];
/** Seconds of the fade-out when a critter bolts, and how fast it grows in when it arrives. */
export const FLEE_FADE_RATE = 0.9;
export const GROW_RATE = 3;
/** Flee speeds for a bird taking off (world units per second, along and up). */
export const BIRD_FLEE_SPEED = 2.6;
export const BIRD_CLIMB = 1.6;
/** The contact shadow: footprint over the critter's modelled length (x across, z along), alpha, and its lift off the ground. */
export const SHADOW: Record<CritterKind, [number, number]> = { rabbit: [0.19, 0.3], bird: [0.1, 0.15] };
export const CRITTER_SHADOW_ALPHA = 0.32;
export const SHADOW_LIFT = 0.012;

export type IdleAct = "nibble" | "look" | "still";
export type Mode = "idle" | "travel" | "flee";

/** The drawn ground under a point (the micro-relief included) — what the feet must touch. */
export function critterGround(x: number, z: number): number { return GroundMesh.drawnAt(x, z); }

export function between([a, b]: readonly [number, number] | readonly number[]): number { return a + Math.random() * (b - a); }
export function damp(from: number, to: number, rate: number, dt: number): number { return to + (from - to) * Math.exp(-rate * dt); }
export function dampAngle(from: number, to: number, rate: number, dt: number): number {
  const d = Math.atan2(Math.sin(to - from), Math.cos(to - from));
  return from + d * (1 - Math.exp(-rate * dt));
}

/** Where a critter may go next: the level's road verge (CritterViews), or the hub's lawn (critterHub.ts). */
export interface CritterWorld { nextSpot(from: Cell): { cell: Cell; at: THREE.Vector3 } | null }

/**
 * Scale over the modelled size (a rabbit's body is ~0.2 of a cell long). Life size (×1, 2026-09-22) was a 5 px
 * brown fleck at the play zoom, sunk to its back in the 0.11-high grass tufts: on the phone the critters were simply
 * gone (Kevin, 2026-09-23). ×1.6 then read as giant and, hovering on the undrawn relief, as floating (Kevin, same
 * day). ×1.15 keeps a rabbit's head (0.2) well under a soldier's (0.33), its ear tips under the soldier's head even
 * sitting up, and its ears clear of the tufts; they now spend their time on the road and its verge, where the
 * scatter is thin, so they read without the extra size.
 */
export const CRITTER_SCALE: Readonly<Record<CritterKind, number>> = { rabbit: 1.15, bird: 1.2 };
/** Spread of a critter's spot inside its cell, world units (± half). */
export const CELL_JITTER = 0.6;
/** How near an enemy must come before a critter bolts, world units. */
export const FLEE_RANGE = 2.6;
